import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { yupResolver } from '@hookform/resolvers/dist/ie11/yup/yup';
import * as yup from 'yup';
import { Alert, Message, Input, Button } from 'components/atoms';
import Confirm from 'hoc/modal/Confirm';
import { Modal } from 'hoc/modal';
import useModal from 'hooks/useModal';
import Footer from 'components/templates/layout/Footer';
import { postAuthSocialSignup } from 'utils/api';
import { menu } from 'utils/constants';
import logo from 'assets/images/logo.png';

const schema = yup.object({
  name: yup.string().required('이름을 입력해주세요.'),
  email: yup
    .string()
    .email('이메일 형식이 올바르지 않습니다.')
    .required('이메일을 입력해주세요.'),
});

const PROVIDER_LABEL = {
  kakao: '카카오',
  naver: '네이버',
};

const OAuthSignup = () => {
  const navigate = useNavigate();
  const [context, setContext] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    show: showConfirm,
    openModal: openConfirm,
    closeModal: closeConfirm,
  } = useModal({ autoClose: false });
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    const raw = sessionStorage.getItem('oauth_signup');
    if (!raw) {
      navigate(menu.login.link, { replace: true });
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setContext(parsed);
      if (parsed.nickname) setValue('name', parsed.nickname);
    } catch (e) {
      navigate(menu.login.link, { replace: true });
    }
  }, [navigate, setValue]);

  const goToLogin = () => navigate(menu.login.link);

  const onSubmit = async (data) => {
    if (!context?.signupToken) return;
    setIsLoading(true);
    setErrorMessage('');
    try {
      const res = await postAuthSocialSignup({
        signupToken: context.signupToken,
        name: data.name,
        email: data.email,
      });
      if (res.statusCode === 200) {
        sessionStorage.removeItem('oauth_signup');
        openConfirm();
      } else {
        setErrorMessage(res.message || '가입 요청에 실패했습니다.');
      }
    } catch (e) {
      const message = e.response?.data?.message || '가입 요청에 실패했습니다.';
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  const providerLabel = PROVIDER_LABEL[context?.provider] || '소셜';

  return (
    <Container>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Logo src={logo} />
          <Title>
            <span style={{ fontWeight: 300 }}>
              &nbsp;&nbsp;&nbsp;급매 탐색기
            </span>
          </Title>
        </div>
        <SubTitle>{providerLabel} 가입</SubTitle>
        <Contents>이메일 인증을 완료하면 가입이 끝납니다.</Contents>
        <Contents style={{ marginBottom: 20 }}>
          입력하신 이메일로 인증 메일이 발송됩니다.
        </Contents>

        <InputWrapper>
          <Label>
            이름<Required>*</Required>
          </Label>
          <Input
            name="name"
            placeholder="이름을 입력해주세요."
            ref={register}
            variant={errors.name && 'danger'}
            style={{ fontSize: 12, fontWeight: 400, cursor: 'pointer' }}
          />
          {errors.name && (
            <Message variant="danger">{errors.name.message}</Message>
          )}
        </InputWrapper>

        <InputWrapper>
          <Label>
            이메일<Required>*</Required>
          </Label>
          <Input
            name="email"
            type="email"
            placeholder="이메일을 입력해주세요."
            ref={register}
            variant={errors.email && 'danger'}
            style={{ fontSize: 12, fontWeight: 400, cursor: 'pointer' }}
          />
          {errors.email && (
            <Message variant="danger">{errors.email.message}</Message>
          )}
        </InputWrapper>

        <div style={{ margin: '10px 0 30px' }}>
          {errorMessage !== '' && (
            <Alert variant="danger">{errorMessage}</Alert>
          )}
        </div>

        <Button disabled={isLoading}>
          {isLoading ? '처리 중...' : '인증 메일 받기'}
        </Button>
        <div style={{ height: 10 }} />
        <Button
          type="button"
          onClick={goToLogin}
          style={{ backgroundColor: '#fff', color: '#8800ff' }}
        >
          로그인으로 돌아가기
        </Button>

        <Modal show={showConfirm}>
          <Confirm
            onClose={closeConfirm}
            success="확인"
            onOk={goToLogin}
            type="check"
            title="이메일 발송 완료"
          >
            입력하신 주소로 인증 메일이 전송되었습니다.
            <br />
            메일의 버튼을 클릭하면 가입이 완료됩니다.
            <br />
            <br />
            메일을 받지 못하셨다면 '스팸 메일함'을 확인해주세요.
          </Confirm>
        </Modal>
      </Form>
      <Footer />
    </Container>
  );
};

const Container = styled.div`
  width: 30rem;
  margin: auto;
  padding-top: 3rem;
  line-height: 1.5;
  font-size: 0.8rem;

  input {
    height: 50px;
    padding-left: 10px;
    letter-spacing: -0.03rem;
  }

  button {
    height: 50px;
    border-radius: 10px;
    font-weight: 600;
    font-size: 12.8;

    &:hover {
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 40px;
  background-color: #fff;
  padding: 60px 40px;
  border-radius: 21px;
  box-shadow: 5px 5px 5px #eee;
`;

const Title = styled.div`
  font-size: 2rem;
  font-weight: bold;
  text-align: center;
  display: flex;
  align-items: center;
`;

const SubTitle = styled.div`
  margin-top: -15px;
  margin-bottom: 36px;
  font-weight: 500;
  text-align: center;
  font-size: 14.5px;
  line-height: 22px;
`;

const Contents = styled.div`
  margin: 0 auto;
  font-size: 12px;
  font-weight: 500;
`;

const InputWrapper = styled.div`
  margin-top: 15px;
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
  letter-spacing: -0.03rem;
`;

const Required = styled.span`
  color: #8800ff;
  font-weight: 500;
  margin-left: 2px;
`;

const Logo = styled.img`
  width: 150px;
`;

export default OAuthSignup;
